/**
 * Time-related utilities
 */

import { MILLISECONDS_PER_MINUTE, CHORE_URGENT_THRESHOLD_MINUTES } from '../config.js';

/**
 * Get the number of minutes elapsed since midnight today
 * @returns {number} Minutes since midnight
 */
export function getMinutesElapsedToday() {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);

  const elapsed = now - midnight;
  return Math.floor(elapsed / MILLISECONDS_PER_MINUTE);
}

/**
 * Calculate countdown information for a chore due time
 * @param {string} dueTime - Due time in HH:MM format
 * @returns {object} Object with isOverdue, isUrgent, and text properties
 */
export function getChoreCountdown(dueTime) {
  // Handle missing or invalid dueTime
  if (!dueTime || typeof dueTime !== 'string' || !dueTime.includes(':')) {
    return { isOverdue: false, isUrgent: false, text: '' };
  }

  const now = new Date();
  const [hours, minutes] = dueTime.split(':').map(Number);

  const dueDate = new Date();
  dueDate.setHours(hours, minutes, 0, 0);

  const diff = dueDate - now;
  const isOverdue = diff < 0;
  const absDiff = Math.abs(diff);

  const totalMinutes = Math.floor(absDiff / MILLISECONDS_PER_MINUTE);
  const displayHours = Math.floor(totalMinutes / 60);
  const displayMinutes = totalMinutes % 60;

  let text;
  if (isOverdue) {
    if (displayHours > 0) {
      text = `Overdue by ${displayHours}h ${displayMinutes}m`;
    } else {
      text = `Overdue by ${displayMinutes}m`;
    }
  } else {
    if (displayHours > 0) {
      text = `Due in ${displayHours}h ${displayMinutes}m`;
    } else {
      text = `Due in ${displayMinutes}m`;
    }
  }

  const isUrgent = !isOverdue && totalMinutes <= CHORE_URGENT_THRESHOLD_MINUTES;

  return { isOverdue, isUrgent, text };
}

/**
 * Format a datetime string as relative time (e.g., "2 hours ago")
 * @param {string} dateTime - ISO datetime string
 * @returns {string} Relative time description
 */
export function formatRelativeTime(dateTime) {
  const now = new Date();
  const then = new Date(dateTime);
  const diffMs = now - then;
  const isPast = diffMs > 0;
  const absDiffMs = Math.abs(diffMs);

  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  let value, unit;

  if (weeks > 0) {
    value = weeks;
    unit = 'week';
  } else if (days > 0) {
    value = days;
    unit = 'day';
  } else if (hours > 0) {
    value = hours;
    unit = 'hour';
  } else if (minutes > 0) {
    value = minutes;
    unit = 'minute';
  } else {
    value = seconds;
    unit = 'second';
  }

  const plural = value !== 1 ? 's' : '';
  const direction = isPast ? 'ago' : 'from now';

  return `${value} ${unit}${plural} ${direction}`;
}

/**
 * Update all <time> elements with relative time formatting
 */
export function updateTimeElements() {
  jQuery('time[datetime]').each(function () {
    const dateTime = jQuery(this).attr('datetime');
    jQuery(this).text(formatRelativeTime(dateTime));
  });
}
